// Basic
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import RenderDateWithFromTime from '@/common-utils/components/renders/date/RenderDateWithFromTime';
import RenderInternationalizedAmount from '@/common-utils/components/renders/currency/RenderInternationalizedAmount';
// Common
import RENTAL_EVENT_TYPES from '@/app/modules/rentalFiles/enum/RentalEventTypes';
import TRANSACTION_TYPES from '@/app/modules/terminals/enums/TransactionType';
// Lang
import { nsRentalFile } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.rentalEvent
 * @param root0.isExpired
 * @returns {JSX} HistoryCard
 */
function HistoryCard({ rentalEvent, isExpired }) {
    // Constants
    const isTransactionInError = rentalEvent.transactionType === TRANSACTION_TYPES.TNA.name;
    const isEventExpired = isExpired && rentalEvent.eventType === RENTAL_EVENT_TYPES.PAYMENT.name;

    // Functions
    // Get the badge variant
    const getBadgeVariant = () => {
        // If the rental event type is not remote collection
        // To keep the same color variant for the remote collection event
        if (rentalEvent.eventType !== RENTAL_EVENT_TYPES.REMOTE_COLLECTION.name) {
            // If the rental file is expired
            if (isEventExpired) {
                return 'orange';
            }
            // If the transaction is in error and the event type is preauthorization or additional invoice
            else if (isTransactionInError
                && (rentalEvent.eventType === RENTAL_EVENT_TYPES.PREAUTHORIZATION.name
                    || rentalEvent.eventType === RENTAL_EVENT_TYPES.ADDITIONAL_INVOICE.name)) {
                return 'red';
            }
        }

        // return default variant from enum
        return RENTAL_EVENT_TYPES[rentalEvent.eventType]?.variant;
    };
    // Get Amount
    const getAmount = () => {
        // If the rental event type is a remote collection
        // And the transaction type is TNA (no amount)
        if (rentalEvent.eventType === RENTAL_EVENT_TYPES.REMOTE_COLLECTION.name
            && rentalEvent.transactionType === TRANSACTION_TYPES.TNA.name) {
            return 0;
        }
        return rentalEvent.amount ?? 0;
    };

    return (
        <div className='w-[280px] border border-secondary-hover rounded-md bg-muted/30 overflow-hidden shrink-0'>
            {/* Card header */}
            <div
                className={cn('flex items-center justify-between gap-2 px-4 py-3 border-b',
                    `bg-badge-${getBadgeVariant()}-bg`)}
            >
                <Entity
                    className='text-sm text-dark-foreground'
                    entity={`${RENTAL_EVENT_TYPES[rentalEvent.eventType]?.entity}.name`}
                    namespace={RENTAL_EVENT_TYPES[rentalEvent.eventType]?.namespace}
                />
                <RenderInternationalizedAmount
                    amount={getAmount()}
                    currency={rentalEvent.currency}
                    className='text-sm text-dark-foreground'
                />
            </div>
            {/* Card body */}
            <div className='flex flex-col gap-5 pt-2 pb-2 py-4'>
                <div className='text-center h-6'>
                    <Entity
                        className='text-sm text-dark-foreground'
                        entity={
                            isEventExpired ?
                                `${RENTAL_EVENT_TYPES[rentalEvent.eventType]?.entity}.expired`
                                : isTransactionInError ?
                                    `${RENTAL_EVENT_TYPES[rentalEvent.eventType]?.entity}.error`
                                    : `${RENTAL_EVENT_TYPES[rentalEvent.eventType]?.entity}.success`
                        }
                        namespace={nsRentalFile}
                    />
                </div>
                <div className='text-center text-xs text-dark-foreground'>
                    <RenderDateWithFromTime
                        value={rentalEvent.localTransactionDateTime}
                        noRelative
                        noSeconds
                    />
                </div>
            </div>
        </div >
    );
}

HistoryCard.propTypes = {
    rentalEvent: PropTypes.object.isRequired,
    isExpired: PropTypes.bool
};

export default HistoryCard;

